import * as Sentry from '@sentry/react'
import { call, delay } from 'redux-saga/effects'
import { takeEvery, takeLatest } from 'typed-redux-saga'

// Saga crash machinery — web analog of the mobile app's
// ErrorHandlingService.handleSagaError. A single uncaught error in any saga tears
// down the whole saga tree (redux-saga cancels every sibling), silently: the app
// keeps rendering but stops reacting to every action. These helpers make crashes
// observable (reportSagaError/handleSagaError) and recoverable (keepAlive).

// A type alias (not an interface) so it gets an implicit index signature and
// stays assignable to Sentry's `Extras` in the captureException call below.
type SagaErrorContext = {
  sagaName?: string
  sagaStack?: string
  actionType?: string
}

// Must never throw: it runs inside keepAlive's catch and the middleware onError —
// a throwing reporter would permanently kill the saga it is reporting on (e.g.
// logError touches localStorage, which throws when the browser blocks storage).
export function reportSagaError(error: Error, context: SagaErrorContext = {}): void {
  if (__DEV__) {
    // The analytics path below can be silent (pre-init or broken storage).
    console.error('Saga crashed:', context.sagaName ?? '', error)
  }

  try {
    // gtag event only — Sentry is captured below with one severity for all paths.
    if (typeof logError === 'function') {
      logError('web_sagaCrashError', error, context, false)
    }
  } catch {
    // Reporting is best-effort; never break the recovery path over it.
  }

  try {
    Sentry.captureException(error, { level: 'fatal', extra: context })
  } catch {
    // Reporting is best-effort; never break the recovery path over it.
  }
}

// Backstop passed to createSagaMiddleware({ onError }). Now that each root saga is
// wrapped in `keepAlive`, this should rarely fire — it catches anything that still
// escapes to the root (e.g. an unwrapped fork added later).
export function handleSagaError(error: Error, errorInfo: { sagaStack?: string }): void {
  reportSagaError(error, { sagaStack: errorInfo?.sagaStack })
}

type SagaWorker = (...args: any[]) => any

// Catches at the worker level, where the native stack is still meaningful and the
// triggering action type is known (keepAlive's catch sees neither: redux-saga's
// sagaStack is module-private and already cleared there). Cancellation is not an
// exception in redux-saga, so a superseded takeLatest worker never reports.
function wrapWorkerSafe (pattern: string, worker: SagaWorker) {
  return function * safeWorker (...args: any[]) {
    try {
      yield call(worker, ...args)
    } catch (error) {
      reportSagaError(error as Error, { sagaName: worker.name, actionType: pattern })
    }
  }
}

// Drop-in takeLatest/takeEvery replacements that keep a worker crash contained:
// the watcher stays alive, the domain's all([...]) is not aborted and in-flight
// sibling workers are not cancelled (a cancelled worker would silently drop its
// failure action and never settle its resolve/reject callbacks). keepAlive in
// RootSaga remains the outer backstop for anything these don't cover.
export function takeLatestSafe (pattern: string, worker: SagaWorker) {
  return takeLatest(pattern, wrapWorkerSafe(pattern, worker))
}

export function takeEverySafe (pattern: string, worker: SagaWorker) {
  return takeEvery(pattern, wrapWorkerSafe(pattern, worker))
}

// Consider a run healthy (reset backoff escalation) once the saga survived this long.
const HEALTHY_RUN_MS = 60000
const MAX_RESTART_DELAY_MS = 10000
// Cap crash reports per crash streak so a permanent crash-loop can't flood
// analytics; the saga itself keeps restarting beyond the cap, just silently.
// The budget refreshes after a healthy run, so a later, different bug in the
// same saga still gets reported.
const MAX_REPORTED_CRASHES = 5
// Session-lifetime ceiling per saga: the streak budget refreshes after healthy
// runs, so slow-cadence crashers need an absolute bound too.
const MAX_TOTAL_REPORTED_CRASHES = 20

// Keep a saga alive across uncaught errors: report the crash and restart only that
// saga, so siblings keep running. The first crash after a healthy run restarts
// instantly; consecutive quick crashes back off up to MAX_RESTART_DELAY_MS.
export function * keepAlive(saga: () => Generator, name: string) {
  let consecutiveCrashes = 0
  let reportedCrashes = 0
  let totalReportedCrashes = 0

  while (true) {
    // Monotonic clock: wall-clock jumps must not fake a healthy run.
    const runStartedAt = performance.now()
    try {
      yield call(saga)
      // A watcher saga never returns; if it does, don't hot-loop restarting it.
      break
    } catch (error) {
      // Only actual run time counts as healthy; the restart delay is excluded.
      if (performance.now() - runStartedAt >= HEALTHY_RUN_MS) {
        consecutiveCrashes = 1
        reportedCrashes = 0
      } else {
        consecutiveCrashes++
      }

      if (reportedCrashes < MAX_REPORTED_CRASHES && totalReportedCrashes < MAX_TOTAL_REPORTED_CRASHES) {
        reportedCrashes++
        totalReportedCrashes++
        reportSagaError(error as Error, { sagaName: name })
      }

      yield delay(Math.min(1000 * (consecutiveCrashes - 1), MAX_RESTART_DELAY_MS))
    }
  }
}
